import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
interface PrivacyModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function PrivacyModal({
  isOpen,
  onClose
}: PrivacyModalProps) {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);
  return <AnimatePresence data-id="element-124">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={onClose} data-id="element-125" />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-126">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }} className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-127">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-128">
                <h2 className="text-2xl font-bold text-[#2C3E50]" data-id="element-129">
                  Política de Privacidad y Protección de Datos
                </h2>
                <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Cerrar" data-id="element-130">
                  <X className="w-5 h-5 text-gray-500" data-id="element-131" />
                </button>
              </div>

              {/* Content */}
              <div className="overflow-y-auto p-6 space-y-6 text-[#2C3E50]" data-id="element-132">
                <section data-id="element-133">
                  <h3 className="text-lg font-semibold mb-3 text-[#5B9BD5]" data-id="element-134">
                    1. INTRODUCCIÓN
                  </h3>
                  <p className="text-gray-700 leading-relaxed" data-id="element-135">
                    En la Cámara Ecuatoriana de Emprendimiento y Desarrollo
                    Sostenible (CEDES) valoramos y respetamos la privacidad de
                    nuestros usuarios. Esta Política de Privacidad describe cómo
                    recopilamos, utilizamos, almacenamos y protegemos los datos
                    personales que usted nos proporciona al utilizar nuestro
                    sitio web www.cedes.ec u otros canales de comunicación.
                  </p>
                </section>

                <section data-id="element-136">
                  <h3 className="text-lg font-semibold mb-3 text-[#5B9BD5]" data-id="element-137">
                    2. RESPONSABLE DEL TRATAMIENTO
                  </h3>
                  <div className="text-gray-700 leading-relaxed space-y-2" data-id="element-138">
                    <p data-id="element-139">
                      <strong data-id="element-140">Nombre de la entidad:</strong> Cámara Ecuatoriana
                      de Emprendimiento y Desarrollo Sostenible (CEDES).
                    </p>
                    <p data-id="element-141">
                      <strong data-id="element-142">Dirección Legal:</strong> Edificio The Point,
                      Oficina 1803, Puerto Santa Ana, Guayaquil, Ecuador.
                    </p>
                    <p data-id="element-143">
                      <strong data-id="element-144">Correo electrónico de contacto:</strong>{' '}
                      contacto@cedes.ec.
                    </p>
                  </div>
                </section>

                <section data-id="element-145">
                  <h3 className="text-lg font-semibold mb-3 text-[#5B9BD5]" data-id="element-146">
                    3. DATOS PERSONALES QUE RECOPILAMOS
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-3" data-id="element-147">
                    Podemos recopilar los siguientes datos personales cuando
                    navegas o interactúas con nuestro sitio web u otros canales:
                  </p>
                  <ul className="space-y-2 text-gray-700 leading-relaxed" data-id="element-148">
                    <li className="flex gap-2" data-id="element-149">
                      <span className="text-[#5B9BD5] font-bold" data-id="element-150">•</span>
                      <span data-id="element-151">Nombres y apellidos</span>
                    </li>
                    <li className="flex gap-2" data-id="element-152">
                      <span className="text-[#5B9BD5] font-bold" data-id="element-153">•</span>
                      <span data-id="element-154">Correo electrónico</span>
                    </li>
                    <li className="flex gap-2" data-id="element-155">
                      <span className="text-[#5B9BD5] font-bold" data-id="element-156">•</span>
                      <span data-id="element-157">Número de teléfono</span>
                    </li>
                    <li className="flex gap-2" data-id="element-158">
                      <span className="text-[#5B9BD5] font-bold" data-id="element-159">•</span>
                      <span data-id="element-160">Información de empresa o emprendimiento</span>
                    </li>
                    <li className="flex gap-2" data-id="element-161">
                      <span className="text-[#5B9BD5] font-bold" data-id="element-162">•</span>
                      <span data-id="element-163">
                        Dirección IP y datos de navegación (cookies, ubicación,
                        tipo de navegador)
                      </span>
                    </li>
                    <li className="flex gap-2" data-id="element-164">
                      <span className="text-[#5B9BD5] font-bold" data-id="element-165">•</span>
                      <span data-id="element-166">
                        Información proporcionada mediante formularios de
                        contacto o suscripción
                      </span>
                    </li>
                  </ul>
                </section>

                <section data-id="element-167">
                  <h3 className="text-lg font-semibold mb-3 text-[#5B9BD5]" data-id="element-168">
                    4. FINALIDAD DEL TRATAMIENTO
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-3" data-id="element-169">
                    Recopilamos tus datos con las siguientes finalidades:
                  </p>
                  <ul className="space-y-2 text-gray-700 leading-relaxed" data-id="element-170">
                    <li className="flex gap-2" data-id="element-171">
                      <span className="text-[#5B9BD5] font-bold" data-id="element-172">•</span>
                      <span data-id="element-173">
                        Enviar información institucional, boletines, eventos y
                        servicios de la Cámara
                      </span>
                    </li>
                    <li className="flex gap-2" data-id="element-174">
                      <span className="text-[#5B9BD5] font-bold" data-id="element-175">•</span>
                      <span data-id="element-176">
                        Atender consultas o solicitudes realizadas a través de
                        formularios de contacto
                      </span>
                    </li>
                    <li className="flex gap-2" data-id="element-177">
                      <span className="text-[#5B9BD5] font-bold" data-id="element-178">•</span>
                      <span data-id="element-179">Gestionar inscripciones a eventos o programas</span>
                    </li>
                    <li className="flex gap-2" data-id="element-180">
                      <span className="text-[#5B9BD5] font-bold" data-id="element-181">•</span>
                      <span data-id="element-182">
                        Realizar análisis estadísticos de navegación (uso de
                        cookies)
                      </span>
                    </li>
                    <li className="flex gap-2" data-id="element-183">
                      <span className="text-[#5B9BD5] font-bold" data-id="element-184">•</span>
                      <span data-id="element-185">
                        Cumplir con obligaciones legales y regulatorias
                      </span>
                    </li>
                  </ul>
                </section>

                <section data-id="element-186">
                  <h3 className="text-lg font-semibold mb-3 text-[#5B9BD5]" data-id="element-187">
                    5. LEGITIMACIÓN PARA EL TRATAMIENTO
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-3" data-id="element-188">
                    El tratamiento de tus datos se realiza con base en:
                  </p>
                  <ul className="space-y-2 text-gray-700 leading-relaxed" data-id="element-189">
                    <li className="flex gap-2" data-id="element-190">
                      <span className="text-[#5B9BD5] font-bold" data-id="element-191">•</span>
                      <span data-id="element-192">Tu consentimiento expreso</span>
                    </li>
                    <li className="flex gap-2" data-id="element-193">
                      <span className="text-[#5B9BD5] font-bold" data-id="element-194">•</span>
                      <span data-id="element-195">
                        El interés legítimo de CEDES para cumplir su misión
                        institucional
                      </span>
                    </li>
                    <li className="flex gap-2" data-id="element-196">
                      <span className="text-[#5B9BD5] font-bold" data-id="element-197">•</span>
                      <span data-id="element-198">
                        El cumplimiento de obligaciones legales, según la
                        normativa ecuatoriana aplicable (Ley Orgánica de
                        Protección de Datos Personales – LOPDP)
                      </span>
                    </li>
                  </ul>
                </section>

                <section data-id="element-199">
                  <h3 className="text-lg font-semibold mb-3 text-[#5B9BD5]" data-id="element-200">
                    6. DESTINATARIOS DE LOS DATOS
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-3" data-id="element-201">
                    Tus datos personales no serán compartidos con terceros,
                    salvo que:
                  </p>
                  <ul className="space-y-2 text-gray-700 leading-relaxed" data-id="element-202">
                    <li className="flex gap-2" data-id="element-203">
                      <span className="text-[#5B9BD5] font-bold" data-id="element-204">•</span>
                      <span data-id="element-205">Tengamos tu consentimiento previo</span>
                    </li>
                    <li className="flex gap-2" data-id="element-206">
                      <span className="text-[#5B9BD5] font-bold" data-id="element-207">•</span>
                      <span data-id="element-208">
                        Sea requerido por autoridad competente conforme a la ley
                      </span>
                    </li>
                    <li className="flex gap-2" data-id="element-209">
                      <span className="text-[#5B9BD5] font-bold" data-id="element-210">•</span>
                      <span data-id="element-211">
                        Se usen herramientas tecnológicas de terceros (por
                        ejemplo, para envío de newsletters, CRM o formularios)
                        con contratos que garantizan la protección de tus datos
                      </span>
                    </li>
                  </ul>
                </section>

                <section data-id="element-212">
                  <h3 className="text-lg font-semibold mb-3 text-[#5B9BD5]" data-id="element-213">
                    7. DERECHOS DEL TITULAR DE LOS DATOS
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-3" data-id="element-214">
                    Tienes derecho a:
                  </p>
                  <ul className="space-y-2 text-gray-700 leading-relaxed mb-3" data-id="element-215">
                    <li className="flex gap-2" data-id="element-216">
                      <span className="text-[#5B9BD5] font-bold" data-id="element-217">•</span>
                      <span data-id="element-218">Acceder a tus datos personales</span>
                    </li>
                    <li className="flex gap-2" data-id="element-219">
                      <span className="text-[#5B9BD5] font-bold" data-id="element-220">•</span>
                      <span data-id="element-221">Rectificar datos inexactos o incompletos</span>
                    </li>
                    <li className="flex gap-2" data-id="element-222">
                      <span className="text-[#5B9BD5] font-bold" data-id="element-223">•</span>
                      <span data-id="element-224">Eliminar tus datos (cuando proceda)</span>
                    </li>
                    <li className="flex gap-2" data-id="element-225">
                      <span className="text-[#5B9BD5] font-bold" data-id="element-226">•</span>
                      <span data-id="element-227">Oponerte al tratamiento</span>
                    </li>
                    <li className="flex gap-2" data-id="element-228">
                      <span className="text-[#5B9BD5] font-bold" data-id="element-229">•</span>
                      <span data-id="element-230">Solicitar la portabilidad de tus datos</span>
                    </li>
                    <li className="flex gap-2" data-id="element-231">
                      <span className="text-[#5B9BD5] font-bold" data-id="element-232">•</span>
                      <span data-id="element-233">
                        Revocar el consentimiento en cualquier momento
                      </span>
                    </li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed" data-id="element-234">
                    Puedes ejercer estos derechos escribiéndonos a{' '}
                    <strong data-id="element-235">protecciondatos@cedes.ec</strong>
                  </p>
                </section>

                <section data-id="element-236">
                  <h3 className="text-lg font-semibold mb-3 text-[#5B9BD5]" data-id="element-237">
                    8. USO DE COOKIES
                  </h3>
                  <p className="text-gray-700 leading-relaxed" data-id="element-238">
                    Nuestros sitios utilizan cookies propias y de terceros para
                    mejorar la experiencia de navegación y análisis del tráfico.
                    Puedes aceptar, configurar o rechazar el uso de cookies
                    desde el banner de consentimiento al ingresar al sitio.
                  </p>
                </section>

                <section data-id="element-239">
                  <h3 className="text-lg font-semibold mb-3 text-[#5B9BD5]" data-id="element-240">
                    9. SEGURIDAD DE LA INFORMACIÓN
                  </h3>
                  <p className="text-gray-700 leading-relaxed" data-id="element-241">
                    CEDES aplica medidas de seguridad técnicas y organizativas
                    adecuadas para proteger tus datos personales contra accesos
                    no autorizados, pérdida, destrucción o alteración.
                  </p>
                </section>

                <section data-id="element-242">
                  <h3 className="text-lg font-semibold mb-3 text-[#5B9BD5]" data-id="element-243">
                    10. CONSERVACIÓN DE LOS DATOS
                  </h3>
                  <p className="text-gray-700 leading-relaxed" data-id="element-244">
                    Tus datos serán conservados solo durante el tiempo necesario
                    para cumplir con la finalidad para la que fueron recopilados
                    o mientras exista una relación institucional o contractual.
                    Posteriormente serán eliminados o anonimizados, según
                    corresponda.
                  </p>
                </section>

                <section data-id="element-245">
                  <h3 className="text-lg font-semibold mb-3 text-[#5B9BD5]" data-id="element-246">
                    11. CAMBIOS A ESTA POLÍTICA
                  </h3>
                  <p className="text-gray-700 leading-relaxed" data-id="element-247">
                    Nos reservamos el derecho de modificar esta Política de
                    Privacidad en cualquier momento. Cualquier cambio será
                    publicado en esta página con la fecha de actualización
                    correspondiente.
                  </p>
                </section>

                <section data-id="element-248">
                  <h3 className="text-lg font-semibold mb-3 text-[#5B9BD5]" data-id="element-249">
                    12. AUTORIDAD DE CONTROL
                  </h3>
                  <p className="text-gray-700 leading-relaxed" data-id="element-250">
                    Si consideras que tus derechos no han sido respetados,
                    puedes presentar un reclamo ante la Autoridad de Protección
                    de Datos Personales de Ecuador, la Superintendencia de
                    Protección de Datos Personales.
                  </p>
                </section>

                <section data-id="element-251">
                  <h3 className="text-lg font-semibold mb-3 text-[#5B9BD5]" data-id="element-252">
                    13. VIGENCIA
                  </h3>
                  <p className="text-gray-700 leading-relaxed" data-id="element-253">
                    La vigencia de estas Políticas de Privacidad y Protección de
                    Datos es desde el 21 de noviembre de 2025 y permanecerá
                    vigente hasta que exista una nueva versión que la reemplace.
                  </p>
                </section>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-gray-200" data-id="element-254">
                <button onClick={onClose} className="w-full bg-[#5B9BD5] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#4A8AC4] transition-colors" data-id="element-255">
                  Cerrar
                </button>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}